import { t } from '@app/i18n';
import type { ApiBody, ApiData } from '@app/api/typed';
import type { components } from '@app/api/types';
import type { ApiError } from '@app/api/ApiError';

/**
 * Modelo da página «Admissão no cluster» (aim-xsk.8), aberta a partir da lista de Sites. O protótipo aprovado é
 * `docs/especificacao/prototipo-admissao-no-site.md`.
 */

export type SiteAdmission = ApiData<'/api/v1/sites/{id}/admission'>;
export type AdmissionSettings = components['schemas']['AdmissionSettings'];
export type AdmissionVersion = components['schemas']['AdmissionVersion'];
export type AdmissionChange = components['schemas']['AdmissionChange'];
export type AdmissionPreview = ApiData<'/api/v1/sites/{id}/admission/preview', 'post'>;
export type AdmissionSummary = components['schemas']['AdmissionSummary'];
export type AdmissionChip = components['schemas']['AdmissionChip'];
export type AdmissionApply = ApiBody<'/api/v1/sites/{id}/admission/versions', 'post'>;
export type AdmissionToken = ApiData<'/api/v1/sites/{id}/admission/token', 'post'>;
export type AdmissionImpact = AdmissionPreview['impact'];

/** O tom do chip, na ordem do protótipo §1: verde aplicada, amarelo aplicando/desatualizada, vermelho sem resposta. */
export function chipTone(chip: AdmissionChip): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (chip) {
    case 'applied':
      return 'success';
    case 'applying':
    case 'outdated':
      return 'warning';
    case 'no_response':
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * O texto do chip. As versões e a hora vêm do resumo gravado; o chip «Não registrada» vale também para o Site que
 * não aparece em `listSiteAdmissions` (nunca gerou token).
 */
export function chipLabel(summary: AdmissionSummary | undefined, when?: string | null): string {
  if (!summary) return t('sites.admission.chip.notRegistered');
  const latest = summary.latest_version ?? 0;
  switch (summary.chip) {
    case 'applied':
      return t('sites.admission.chip.applied', { version: latest });
    case 'applying':
      return t('sites.admission.chip.applying', { version: latest });
    case 'outdated':
      return t('sites.admission.chip.outdated', { applied: summary.applied_version ?? 0, saved: latest });
    case 'no_response':
      return when
        ? t('sites.admission.chip.noResponse', { when })
        : t('sites.admission.chip.noResponse.never');
    case 'never_applied':
      return t('sites.admission.chip.neverApplied');
    default:
      return t('sites.admission.chip.notRegistered');
  }
}

/**
 * Os rótulos do diff, pelo caminho estável que o servidor manda (`AdmissionChange.field`). Mapa explícito, e não
 * uma chave montada com o caminho: assim um campo novo do servidor aparece pelo próprio caminho em vez de virar uma
 * chave de catálogo inexistente, e a guarda de chaves do i18n continua vendo todas as que a tela usa.
 */
const CHANGE_LABELS: Record<string, string> = {
  'identity.spiffe_id_template': 'sites.admission.change.identity.spiffeIdTemplate',
  'gateway.host': 'sites.admission.change.gateway.host',
  'gateway.port': 'sites.admission.change.gateway.port',
  'gateway.base_path': 'sites.admission.change.gateway.basePath',
  'gateway.trusted_ca_bundle': 'sites.admission.change.gateway.trustedCaBundle',
  'sts.enabled': 'sites.admission.change.sts.enabled',
  'sts.host': 'sites.admission.change.sts.host',
  'sts.port': 'sites.admission.change.sts.port',
  'sts.path': 'sites.admission.change.sts.path',
  'sts.timeout_ms': 'sites.admission.change.sts.timeoutMs',
  'sts.peer_spiffe_id': 'sites.admission.change.sts.peerSpiffeId',
  'sts.trust_bundle_name': 'sites.admission.change.sts.trustBundleName',
  'signatures.enforce': 'sites.admission.change.signatures.enforce',
  'signatures.image_globs': 'sites.admission.change.signatures.imageGlobs',
  'signatures.certificates': 'sites.admission.change.signatures.certificates',
  'advanced.helper.image': 'sites.admission.change.advanced.helper.image',
  'advanced.helper.health_port': 'sites.admission.change.advanced.helper.healthPort',
  'advanced.helper.workload_api_path': 'sites.admission.change.advanced.helper.workloadApiPath',
  'advanced.helper.socket': 'sites.admission.change.advanced.helper.socket',
  'advanced.helper.fs_group': 'sites.admission.change.advanced.helper.fsGroup',
  'advanced.helper.certificate_mode': 'sites.admission.change.advanced.helper.certificateMode',
  'advanced.helper.key_mode': 'sites.admission.change.advanced.helper.keyMode',
  'advanced.proxy.image': 'sites.admission.change.advanced.proxy.image',
  'advanced.proxy.listen_port': 'sites.admission.change.advanced.proxy.listenPort',
  'advanced.proxy.health_port': 'sites.admission.change.advanced.proxy.healthPort',
  'advanced.proxy.env_var_name': 'sites.admission.change.advanced.proxy.envVarName',
  'advanced.proxy.connect_timeout': 'sites.admission.change.advanced.proxy.connectTimeout',
  'advanced.proxy.request_timeout': 'sites.admission.change.advanced.proxy.requestTimeout',
  'advanced.proxy.stream_idle_timeout': 'sites.admission.change.advanced.proxy.streamIdleTimeout',
  'advanced.proxy.upstream_idle_timeout': 'sites.admission.change.advanced.proxy.upstreamIdleTimeout',
  'advanced.proxy.cpu_request': 'sites.admission.change.advanced.proxy.cpuRequest',
  'advanced.proxy.cpu_limit': 'sites.admission.change.advanced.proxy.cpuLimit',
  'advanced.proxy.memory_request': 'sites.admission.change.advanced.proxy.memoryRequest',
  'advanced.proxy.memory_limit': 'sites.admission.change.advanced.proxy.memoryLimit',
  'releases.count': 'sites.admission.change.releases.count',
};

export function changeLabel(field: string): string {
  const key = CHANGE_LABELS[field];
  return key ? t(key) : field;
}

/** O valor de um lado do diff: vazio é «—», e os booleanos viram ligado/desligado. */
export function changeValue(field: string, value: string): string {
  if (value === '') return '—';
  if (field.endsWith('.enabled') || field.endsWith('.enforce')) {
    return value === 'true' ? t('sites.admission.change.on') : t('sites.admission.change.off');
  }
  return value;
}

/** O comando do protótipo §4, com o token e o prazo. O nome do chart é o da instalação de hoje. */
export function installCommand(token: string, expiresAt: string): string {
  return [
    'helm install segura-aim … \\',
    `  --set segura-agent-admission.registration.token=${token} \\`,
    `  --set segura-agent-admission.registration.expiresAt=${expiresAt}`,
  ].join('\n');
}

/** Os dois primeiros e os dois últimos bytes do fingerprint, como o protótipo mostra («SHA256:4f:9a:…»). */
export function shortFingerprint(sha256: string): string {
  if (!sha256) return '—';
  const pairs = sha256.match(/.{2}/g) ?? [];
  if (pairs.length <= 4) return `SHA256:${pairs.join(':')}`;
  return `SHA256:${pairs.slice(0, 2).join(':')}:…:${pairs.slice(-2).join(':')}`;
}

/** O caminho do erro de campo do servidor (`settings.gateway.host`) no nome do formulário (`gateway.host`). */
export function formPath(serverField: string): string | null {
  return serverField.startsWith('settings.') ? serverField.slice('settings.'.length) : null;
}

/**
 * Cópia profunda das configurações, com as listas sempre presentes. O formulário edita esta cópia; o «Descartar»
 * volta à da última versão (ou aos padrões, sem versão).
 */
export function cloneSettings(settings: AdmissionSettings): AdmissionSettings {
  return JSON.parse(JSON.stringify(settings)) as AdmissionSettings;
}

/** Igualdade por conteúdo: o «Revisar e aplicar» fica disponível também sem mudança (a versão 1 é igual aos padrões). */
export function sameSettings(a: AdmissionSettings, b: AdmissionSettings): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** O código que acompanha `admission_refused`, quando o servidor o mandou. */
export function admissionCode(error: ApiError | null | undefined): string | null {
  if (!error || error.code !== 'admission_refused') return null;
  return error.data.admission_code || null;
}
